// CastLayout3D.tsx — 3D perspective view of the casting yard layout.
// Shows panels flat on slab, LR1300 crawler crane, and when animation is active:
// realistic tilt-up sequence (flat → tilt vertical → lift → swing → set → brace → release).

import { useRef, useMemo, useEffect, useState } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { Grid, Environment, Text, Line } from '@react-three/drei';
import GridLabels, { snapGridExtent } from './viewer/GridLabels';
import * as THREE from 'three';
import { useShallowStore } from '../store/useStore';
import type { CastLayoutResult, CastPlacedPanel } from '../utils/castLayout';
import type { CastLayoutSettings, PanelData, PickStep } from '../types/panelvar';
import type { InstalledPosition } from '../utils/installedPositions';
import { filteredRings } from '../utils/craneCapacity';
import OrbitCameraRig from './viewer/OrbitCameraRig';
import ViewerToolbar from './viewer/ViewerToolbar';
import NorthArrow from './viewer/NorthArrow';

interface Props {
  result:              CastLayoutResult;
  slabCorners:         [number, number][];
  settings:            CastLayoutSettings;
  currentPickStep?:    PickStep | null;
  allPickSteps?:       PickStep[];
  installedPositions?: Map<string, InstalledPosition>;
  selectedPanelIds?:   Set<string>;
  onPanelSelect?:      (id: string | null) => void;
  onRubberBandSelect?: (ids: string[]) => void;
  onPanelMove?:        (id: string, cx: number, cy: number) => void;
  onPanelRotate?:      (id: string, rotRad: number) => void;
  onPanelDrop?:        (id: string) => void;
}

// Plan coords → Three.js: planX → X, planY → +Z (ShapeGeometry rotated [-π/2,0,0])
const INCH = 1 / 12; // 1 inch = 1/12 ft in Three.js (1 unit = 1 ft)
const PANEL_THICKNESS_IN = 7.25;

// ── LR1300 crawler crane — real-scale dimensions in feet (1 unit = 1 ft) ─────
const TRACK_W      = 10.5;
const TRACK_H      =  4.5;
const TRACK_D      = 40;
const TRACK_SPREAD = 29.5;
const BODY_W_CRN   = 36;
const BODY_H_CRN   = 15;
const BODY_D_CRN   = 18;
const CW_W         = 28;
const CW_H         =  6;
const CW_D         = 10;
const CAB_W        = 12;
const CAB_H        = 10;
const CAB_D        = 11;
const BOOM_LEN     = 118;
const BOOM_ANG     = Math.PI / 4;
const MAST_LEN     = 42;
const MAST_ANG     = Math.PI * 0.35;
const BOOM_BASE_Y  = TRACK_H + BODY_H_CRN;
const BOOM_TIP_Y   = BOOM_BASE_Y + BOOM_LEN * Math.cos(BOOM_ANG);
const BOOM_TIP_REACH = BODY_D_CRN * 0.3 + BOOM_LEN * Math.sin(BOOM_ANG);

// Convert plan bearing (atan2 Y-down) to Three.js Y-rotation (atan2(dX, dZ))
function bearingToThreeY(craneArmAngleRad: number): number {
  // craneArmAngleRad = atan2(planY - craneY, planX - craneX)
  // In Three.js: dX = planDx * INCH, dZ = planDy * INCH
  // Three.js Y-rotation to face direction (dX, dZ): atan2(dX, dZ)
  // plan bearing: atan2(planDy, planDx) → dX = cos(bearing), dZ = sin(bearing)
  return Math.atan2(Math.cos(craneArmAngleRad), Math.sin(craneArmAngleRad));
}

function SlabMesh({ corners }: { corners: [number, number][] }) {
  const geo = useMemo(() => {
    if (corners.length < 3) return null;
    const shape = new THREE.Shape();
    shape.moveTo(corners[0][0] * INCH, -corners[0][1] * INCH);
    for (let i = 1; i < corners.length; i++) {
      shape.lineTo(corners[i][0] * INCH, -corners[i][1] * INCH);
    }
    shape.closePath();
    return new THREE.ShapeGeometry(shape);
  }, [corners]);
  if (!geo) return null;
  return (
    <mesh geometry={geo} rotation={[-Math.PI / 2, 0, 0]}>
      <meshStandardMaterial color="#c8c0b0" roughness={0.9} />
    </mesh>
  );
}

function FlatPanel({ cx, cy, w, h, rotationRad, isStacked, stackLevel, panel, weightTons, pickOrder, dimmed, panelId, isSelected, onPanelDown, onPanelRotate: onPanelRotateProp }: {
  cx: number; cy: number; w: number; h: number;
  rotationRad: number; isStacked: boolean; stackLevel: number;
  panel: PanelData; weightTons: number; pickOrder: number;
  dimmed?: boolean;
  panelId: string;
  isSelected?: boolean;
  onPanelDown?: (id: string, offsetX: number, offsetZ: number) => void;
  onPanelRotate?: (id: string, newRotRad: number) => void;
}) {
  const thickness = PANEL_THICKNESS_IN * INCH;
  const yPos = stackLevel * (PANEL_THICKNESS_IN + 2) * INCH;
  const topY = yPos + thickness + 0.05;
  const mark = panel.mpvar.panelMark;
  const labelSize = Math.max(0.5, Math.min(w, h) * INCH * 0.08);
  const panelColor = dimmed ? '#8a8070' : (isStacked ? '#cdc4b0' : '#e8dfc8');

  const openings = [
    ...panel.rovar.filter(s => s.enabled).map(s => s.data),
    ...panel.wdvar.filter(s => s.enabled).map(s => s.data),
    ...panel.drvar.filter(s => s.enabled).map(s => s.data),
    ...panel.dlvar.filter(s => s.enabled).map(s => s.data),
    ...panel.sbvar.filter(s => s.enabled).map(s => s.data),
  ];

  return (
    <group
      position={[cx * INCH, 0, cy * INCH]}
      rotation={[0, -rotationRad, 0]}
      userData={onPanelDown ? { panelId, draggable: true } : undefined}
      onPointerDown={(e) => {
        if (!onPanelDown) return;
        e.stopPropagation();
        onPanelDown(panelId, e.point.x - cx * INCH, e.point.z - cy * INCH);
      }}
      onContextMenu={(e) => {
        e.stopPropagation();
        onPanelRotateProp?.(panelId, rotationRad + Math.PI / 2);
      }}
    >
      {/* Selection highlight */}
      {isSelected && (
        <mesh position={[0, yPos + thickness / 2, 0]}>
          <boxGeometry args={[w * INCH + 1.5, thickness + 0.3, h * INCH + 1.5]} />
          <meshStandardMaterial color="#4da6ff" wireframe />
        </mesh>
      )}
      <mesh position={[0, yPos + thickness / 2, 0]}>
        <boxGeometry args={[w * INCH, thickness, h * INCH]} />
        <meshStandardMaterial color={panelColor} roughness={0.7} opacity={dimmed ? 0.5 : 1} transparent={dimmed} />
      </mesh>

      {!dimmed && mark && (
        <Text
          position={[0, topY, -(labelSize * 0.8)]}
          rotation={[-Math.PI / 2, 0, 0]}
          fontSize={labelSize}
          color="#1a1208"
          anchorX="center"
          anchorY="middle"
          maxWidth={w * INCH * 0.85}
        >
          {mark}
        </Text>
      )}

      {!dimmed && (
        <Text
          position={[0, topY, mark ? labelSize * 0.8 : 0]}
          rotation={[-Math.PI / 2, 0, 0]}
          fontSize={labelSize * 1.2}
          color="#333300"
          anchorX="center"
          anchorY="middle"
        >
          {(weightTons * 2).toFixed(1)}k
        </Text>
      )}

      <mesh position={[-w / 2 * INCH + labelSize * 0.5, topY + 0.04, -h / 2 * INCH + labelSize * 0.5]}>
        <cylinderGeometry args={[labelSize * 0.55, labelSize * 0.55, 0.08, 12]} />
        <meshStandardMaterial color={dimmed ? '#333' : '#1a3a5c'} />
      </mesh>
      {!dimmed && (
        <Text
          position={[-w / 2 * INCH + labelSize * 0.5, topY + 0.09, -h / 2 * INCH + labelSize * 0.5]}
          rotation={[-Math.PI / 2, 0, 0]}
          fontSize={labelSize * 0.65}
          color="#aad4ff"
          anchorX="center"
          anchorY="middle"
          outlineWidth={0.03}
          outlineColor="#000000"
        >
          {String(pickOrder)}
        </Text>
      )}

      {!dimmed && openings.map((f, i) => (
        <mesh key={`void-${i}`} position={[(f.x - w / 2) * INCH, topY, (f.y - h / 2) * INCH]}>
          <boxGeometry args={[f.width * INCH, 0.06, f.height * INCH]} />
          <meshStandardMaterial color="#1a1a1a" />
        </mesh>
      ))}

      {!dimmed && panel.rbvar.filter(s => s.enabled).map((s, i) => (
        <mesh key={`rb-${i}`} position={[(s.data.x - w / 2) * INCH, topY, (s.data.y - h / 2) * INCH]}>
          <cylinderGeometry args={[s.data.diameter / 2 * INCH, s.data.diameter / 2 * INCH, 0.06, 12]} />
          <meshStandardMaterial color="#1a1a1a" />
        </mesh>
      ))}

      {!dimmed && panel.ppvar.filter(s => s.enabled).map((s, i) => (
        <mesh key={`pp-${i}`} position={[(s.data.x - w / 2) * INCH, topY + 0.03, (s.data.y - h / 2) * INCH]}>
          <cylinderGeometry args={[0.28, 0.28, 0.12, 8]} />
          <meshStandardMaterial color="#22aa44" metalness={0.3} roughness={0.4} />
        </mesh>
      ))}

      {!dimmed && panel.bpvar.filter(s => s.enabled).map((s, i) => (
        <mesh key={`bp-${i}`} position={[(s.data.x - w / 2) * INCH, topY + 0.03, (s.data.y - h / 2) * INCH]}>
          <boxGeometry args={[0.4, 0.12, 0.4]} />
          <meshStandardMaterial color="#ff6600" metalness={0.3} roughness={0.4} />
        </mesh>
      ))}
    </group>
  );
}

// ── Installed (erected) panel — standing vertical at wall position ────────────
function InstalledPanel({ instX, instY, w, h, wallAngle, panel }: {
  instX: number; instY: number;
  w: number; h: number;
  wallAngle: number;
  panel: PanelData;
}) {
  const thickness = PANEL_THICKNESS_IN * INCH;
  const wFt  = w * INCH;
  const hFt  = h * INCH;
  const mark = panel.mpvar.panelMark;
  const labelSize = Math.max(0.5, Math.min(wFt, hFt) * 0.08);

  // Panel stands with width along wall direction, height vertical.
  // Y-rotation: wall goes along the wall.angle direction in plan.
  // In plan: along-wall = (cos(wallAngle), -sin(wallAngle)). In Three.js XZ:
  //   plan dX → Three.js dX, plan dY → Three.js dZ
  //   along-wall in 3D = (cos(wallAngle), 0, -sin(wallAngle))
  //   atan2(dX, dZ) for Y-rotation facing that direction: atan2(cos, -sin) = π/2 - wallAngle...
  //   but we want width along this direction, not facing it.
  //   boxGeometry: width (X) dimension goes along local X = world X rotated by rotY.
  //   We want local X → along-wall direction.
  //   Y-rotation θ: local X → (cos θ, 0, sin θ). We need (cos θ, 0, sin θ) = (cos(wallAngle), 0, -sin(wallAngle))
  //   So θ = wallAngle with sin negated → θ = -wallAngle (plan Y-down to 3D Z-positive flip)
  const rotY = -wallAngle;

  const openings = [
    ...panel.rovar.filter(s => s.enabled).map(s => s.data),
    ...panel.wdvar.filter(s => s.enabled).map(s => s.data),
    ...panel.drvar.filter(s => s.enabled).map(s => s.data),
    ...panel.dlvar.filter(s => s.enabled).map(s => s.data),
    ...panel.sbvar.filter(s => s.enabled).map(s => s.data),
  ];

  return (
    <group position={[instX * INCH, hFt / 2, instY * INCH]} rotation={[0, rotY, 0]}>
      {/* Panel body standing vertical */}
      <mesh>
        <boxGeometry args={[wFt, hFt, thickness]} />
        <meshStandardMaterial color="#c8c0a8" roughness={0.75} />
      </mesh>

      {/* Mark on front face */}
      {mark && (
        <Text
          position={[0, 0, thickness / 2 + 0.05]}
          fontSize={labelSize}
          color="#1a1208"
          anchorX="center"
          anchorY="middle"
          maxWidth={wFt * 0.85}
        >
          {mark}
        </Text>
      )}

      {/* Void openings */}
      {openings.map((f, i) => (
        <mesh key={`void-${i}`} position={[(f.x - w / 2) * INCH, (f.y - h / 2) * INCH, thickness / 2 + 0.03]}>
          <boxGeometry args={[f.width * INCH, f.height * INCH, 0.06]} />
          <meshStandardMaterial color="#1a1a1a" />
        </mesh>
      ))}

      {panel.rbvar.filter(s => s.enabled).map((s, i) => (
        <mesh key={`rb-${i}`} position={[(s.data.x - w / 2) * INCH, (s.data.y - h / 2) * INCH, thickness / 2 + 0.03]}>
          <cylinderGeometry args={[s.data.diameter / 2 * INCH, s.data.diameter / 2 * INCH, 0.06, 12]} />
          <meshStandardMaterial color="#1a1a1a" />
        </mesh>
      ))}
    </group>
  );
}

// ── Animated panel — tilts from flat to vertical, lifts, swings ──────────────
// Uses lerp interpolation each frame for smooth motion between discrete steps.
function AnimatedPanel({ pp, step, instPos }: {
  pp: { cx: number; cy: number; w: number; h: number; rotationRad: number; panel: PanelData };
  step: PickStep;
  instPos: InstalledPosition | null;
}) {
  const thickness = PANEL_THICKNESS_IN * INCH;
  const wFt = pp.w * INCH;
  const hFt = pp.h * INCH;

  // Lerp state refs
  const tiltRef  = useRef(0);
  const elevRef  = useRef(0);
  const cxRef    = useRef(pp.cx);
  const cyRef    = useRef(pp.cy);
  // Lerp group ref
  const groupRef = useRef<THREE.Group>(null);

  const targetCx   = step.panelCx;
  const targetCy   = step.panelCy;
  const targetTilt = step.panelTiltAngle;
  const targetElev = step.panelElevFt;

  // Tilt direction: from cast position toward installed position (or panel bearing)
  const instX = instPos?.x ?? (pp.cx + Math.cos(pp.rotationRad) * 120);
  const instY = instPos?.y ?? (pp.cy - Math.sin(pp.rotationRad) * 120);
  const dxInst = (instX - pp.cx) * INCH;
  const dzInst = (instY - pp.cy) * INCH;
  const distInst = Math.sqrt(dxInst * dxInst + dzInst * dzInst) || 1;
  // Y-rotation so local +Z faces install direction
  const tiltFacingY = Math.atan2(dxInst, dzInst);

  useFrame((_, delta) => {
    const speed = 4;
    const k = 1 - Math.exp(-speed * delta);
    tiltRef.current  += (targetTilt - tiltRef.current)  * k;
    elevRef.current  += (targetElev - elevRef.current)  * k;
    cxRef.current    += (targetCx   - cxRef.current)    * k;
    cyRef.current    += (targetCy   - cyRef.current)    * k;

    if (!groupRef.current) return;

    const cx   = cxRef.current;
    const cy   = cyRef.current;
    const tilt = tiltRef.current;
    const elev = elevRef.current;

    // Pivot = near-wall edge of panel (h/2 in tilt direction from center)
    const pivX = cx * INCH + (hFt / 2) * (dxInst / distInst);
    const pivZ = cy * INCH + (hFt / 2) * (dzInst / distInst);

    groupRef.current.position.set(pivX, elev, pivZ);
    groupRef.current.rotation.set(0, tiltFacingY, 0);

    // Inner tilt group child
    const inner = groupRef.current.children[0] as THREE.Group;
    if (inner) {
      // tiltAngle=0 → rotX=-π/2 (flat), tiltAngle=π/2 → rotX=0 (vertical)
      inner.rotation.set(tilt - Math.PI / 2, 0, 0);
    }
  });

  const mark = pp.panel.mpvar.panelMark;
  const labelSize = Math.max(0.5, Math.min(wFt, hFt) * 0.08);

  const openings = [
    ...pp.panel.rovar.filter(s => s.enabled).map(s => s.data),
    ...pp.panel.wdvar.filter(s => s.enabled).map(s => s.data),
    ...pp.panel.drvar.filter(s => s.enabled).map(s => s.data),
    ...pp.panel.dlvar.filter(s => s.enabled).map(s => s.data),
    ...pp.panel.sbvar.filter(s => s.enabled).map(s => s.data),
  ];

  return (
    <group ref={groupRef}>
      {/* Inner group handles tilt rotation around pivot */}
      <group>
        {/* Panel body: center at (0, h/2, 0) = standing, bottom edge at group origin */}
        <mesh position={[0, hFt / 2, 0]}>
          <boxGeometry args={[wFt, hFt, thickness]} />
          <meshStandardMaterial color="#ff8822" roughness={0.6} metalness={0.05} />
        </mesh>

        {/* Openings */}
        {openings.map((f, i) => (
          <mesh key={`void-${i}`} position={[(f.x - pp.w / 2) * INCH, (f.y - pp.h / 2) * INCH + hFt / 2, thickness / 2 + 0.03]}>
            <boxGeometry args={[f.width * INCH, f.height * INCH, 0.06]} />
            <meshStandardMaterial color="#1a1a1a" />
          </mesh>
        ))}

        {pp.panel.rbvar.filter(s => s.enabled).map((s, i) => (
          <mesh key={`rb-${i}`} position={[(s.data.x - pp.w / 2) * INCH, (s.data.y - pp.h / 2) * INCH + hFt / 2, thickness / 2 + 0.03]}>
            <cylinderGeometry args={[s.data.diameter / 2 * INCH, s.data.diameter / 2 * INCH, 0.06, 12]} />
            <meshStandardMaterial color="#1a1a1a" />
          </mesh>
        ))}

        {/* Pick point inserts — bright green discs */}
        {pp.panel.ppvar.filter(s => s.enabled).map((s, i) => (
          <mesh key={`pp-${i}`} position={[(s.data.x - pp.w / 2) * INCH, (s.data.y - pp.h / 2) * INCH + hFt / 2, thickness / 2 + 0.04]}>
            <cylinderGeometry args={[0.35, 0.35, 0.14, 8]} />
            <meshStandardMaterial color="#22ff66" metalness={0.4} roughness={0.3} emissive="#006622" emissiveIntensity={0.3} />
          </mesh>
        ))}

        {/* Mark label */}
        {mark && (
          <Text
            position={[0, hFt / 2, thickness / 2 + 0.05]}
            fontSize={labelSize}
            color="#ffffff"
            anchorX="center"
            anchorY="middle"
            maxWidth={wFt * 0.85}
            outlineWidth={0.04}
            outlineColor="#ff5500"
          >
            {mark}
          </Text>
        )}
      </group>
    </group>
  );
}

// ── Rigging cables from boom hook to panel pick points ────────────────────────
function RiggingCables({ craneX, craneY, facingAngle, panelPickPoints }: {
  craneX: number; craneY: number;
  facingAngle: number;
  panelPickPoints: THREE.Vector3[];
}) {
  // Boom hook world position (tip of boom at full height)
  const hookX = craneX * INCH + BOOM_TIP_REACH * Math.sin(facingAngle);
  const hookZ = craneY * INCH + BOOM_TIP_REACH * Math.cos(facingAngle);
  const hookY = BOOM_TIP_Y;

  const lineObjects = useMemo(() => {
    return panelPickPoints.map(pp => {
      const pts = [new THREE.Vector3(hookX, hookY, hookZ), pp];
      const geo = new THREE.BufferGeometry().setFromPoints(pts);
      const mat = new THREE.LineBasicMaterial({ color: '#ffcc44' });
      return new THREE.Line(geo, mat);
    });
  }, [hookX, hookY, hookZ, panelPickPoints]);

  return (
    <>
      {lineObjects.map((line, i) => (
        <primitive key={i} object={line} />
      ))}
    </>
  );
}

// ── Temporary steel kicker braces ────────────────────────────────────────────
function BraceLines({ instX, instY, w, h, wallAngle, braceCount }: {
  instX: number; instY: number;
  w: number; h: number;
  wallAngle: number;
  braceCount: number;
}) {
  if (braceCount === 0) return null;

  const hFt = h * INCH;
  const wFt = w * INCH;

  // Outward normal from wall (perpendicular outward)
  const outNormX = -Math.sin(wallAngle);
  const outNormZ =  Math.cos(wallAngle);

  // Brace attachment heights on panel (at 2/3 panel height)
  const attachY = hFt * 0.67;
  // Brace foot on slab: 10 ft outward from panel base
  const braceReach = 10;
  const footY = 0;

  // Along-wall direction for distributing multiple braces
  const alongX =  Math.cos(wallAngle);
  const alongZ = -Math.sin(wallAngle);

  // Distribute brace attachment points along panel width
  const offsets: number[] = [];
  const count = Math.min(braceCount, 4);
  for (let i = 0; i < count; i++) {
    offsets.push((i / (count - 1 || 1) - 0.5) * wFt * 0.7);
  }

  const bx = instX * INCH;
  const bz = instY * INCH;

  const braceObjects = useMemo(() => offsets.map(off => {
    const ax = bx + alongX * off;
    const az = bz + alongZ * off;
    const topPt  = new THREE.Vector3(ax, attachY, az);
    const footPt = new THREE.Vector3(ax + outNormX * braceReach, footY, az + outNormZ * braceReach);
    const geo = new THREE.BufferGeometry().setFromPoints([topPt, footPt]);
    const mat = new THREE.LineBasicMaterial({ color: '#aaaaaa' });
    return new THREE.Line(geo, mat);
  }), [bx, bz, attachY, outNormX, outNormZ, braceReach, footY, alongX, alongZ]);

  return (
    <>
      {braceObjects.map((line, i) => (
        <primitive key={i} object={line} />
      ))}
    </>
  );
}

// ── LR1300 crawler crane ──────────────────────────────────────────────────────
function CraneMesh({ craneX, craneY, facingAngle }: {
  craneX: number; craneY: number; facingAngle: number;
}) {
  const fx = craneX * INCH;
  const fz = craneY * INCH;

  const trackY   = TRACK_H / 2;
  const bodyY    = TRACK_H + BODY_H_CRN / 2;
  const cwY      = bodyY;
  const cabY     = bodyY + BODY_H_CRN / 2 + CAB_H / 2;
  const boomBase = TRACK_H + BODY_H_CRN;

  return (
    <group position={[fx, 0, fz]}>
      {/* Undercarriage (fixed) */}
      <mesh position={[-TRACK_SPREAD / 2, trackY, 0]}>
        <boxGeometry args={[TRACK_W, TRACK_H, TRACK_D]} />
        <meshStandardMaterial color="#333" roughness={0.95} />
      </mesh>
      <mesh position={[TRACK_SPREAD / 2, trackY, 0]}>
        <boxGeometry args={[TRACK_W, TRACK_H, TRACK_D]} />
        <meshStandardMaterial color="#333" roughness={0.95} />
      </mesh>
      {[-15, -9, -3, 3, 9, 15].map(dz => (
        <mesh key={`tl-${dz}`} position={[-TRACK_SPREAD / 2, TRACK_H, dz]}>
          <boxGeometry args={[TRACK_W + 1.2, 0.5, 1.0]} />
          <meshStandardMaterial color="#555" roughness={0.9} />
        </mesh>
      ))}
      {[-15, -9, -3, 3, 9, 15].map(dz => (
        <mesh key={`tr-${dz}`} position={[TRACK_SPREAD / 2, TRACK_H, dz]}>
          <boxGeometry args={[TRACK_W + 1.2, 0.5, 1.0]} />
          <meshStandardMaterial color="#555" roughness={0.9} />
        </mesh>
      ))}
      <mesh position={[0, TRACK_H * 0.6, 0]}>
        <boxGeometry args={[TRACK_SPREAD + TRACK_W, TRACK_H * 0.5, TRACK_D * 0.6]} />
        <meshStandardMaterial color="#444" roughness={0.85} />
      </mesh>

      {/* Rotating superstructure */}
      <group rotation={[0, facingAngle, 0]}>
        <mesh position={[0, bodyY, 0]}>
          <boxGeometry args={[BODY_W_CRN, BODY_H_CRN, BODY_D_CRN]} />
          <meshStandardMaterial color="#e8c840" roughness={0.55} metalness={0.25} />
        </mesh>
        <mesh position={[0, cwY, -BODY_D_CRN / 2 - CW_D / 2]}>
          <boxGeometry args={[CW_W, CW_H, CW_D]} />
          <meshStandardMaterial color="#555" roughness={0.7} />
        </mesh>
        <mesh position={[-BODY_W_CRN * 0.25, cabY, BODY_D_CRN * 0.15]}>
          <boxGeometry args={[CAB_W, CAB_H, CAB_D]} />
          <meshStandardMaterial color="#e8c840" roughness={0.45} metalness={0.2} />
        </mesh>
        <mesh position={[-BODY_W_CRN * 0.25, cabY + CAB_H * 0.15, BODY_D_CRN * 0.15 + CAB_D / 2]}>
          <boxGeometry args={[CAB_W * 0.8, CAB_H * 0.5, 0.4]} />
          <meshStandardMaterial color="#88aacc" roughness={0.1} metalness={0.6} opacity={0.7} transparent />
        </mesh>

        {/* Main boom */}
        <group position={[0, boomBase, BODY_D_CRN * 0.3]} rotation={[BOOM_ANG, 0, 0]}>
          {[-1.0, 1.0].map(ox => [-1.0, 1.0].map(oz => (
            <mesh key={`${ox}-${oz}`} position={[ox, BOOM_LEN / 2, oz]}>
              <boxGeometry args={[0.7, BOOM_LEN, 0.7]} />
              <meshStandardMaterial color="#c8a820" roughness={0.5} metalness={0.3} />
            </mesh>
          )))}
          {[20, 40, 60, 80, 100].map(h => (
            <mesh key={h} position={[0, h, 0]}>
              <boxGeometry args={[2.5, 0.5, 2.5]} />
              <meshStandardMaterial color="#c8a820" roughness={0.5} metalness={0.3} />
            </mesh>
          ))}
          <mesh position={[0, BOOM_LEN + 2, 0]}>
            <boxGeometry args={[2.5, 3, 2.0]} />
            <meshStandardMaterial color="#222" roughness={0.8} />
          </mesh>
        </group>

        {/* A-frame mast */}
        <group position={[0, boomBase, -BODY_D_CRN * 0.15]} rotation={[-MAST_ANG, 0, 0]}>
          <mesh position={[-4, MAST_LEN / 2, 0]}>
            <boxGeometry args={[1.5, MAST_LEN, 1.5]} />
            <meshStandardMaterial color="#aaa" roughness={0.55} metalness={0.35} />
          </mesh>
          <mesh position={[4, MAST_LEN / 2, 0]}>
            <boxGeometry args={[1.5, MAST_LEN, 1.5]} />
            <meshStandardMaterial color="#aaa" roughness={0.55} metalness={0.35} />
          </mesh>
          <mesh position={[0, MAST_LEN, 0]}>
            <boxGeometry args={[9, 1.0, 1.5]} />
            <meshStandardMaterial color="#aaa" roughness={0.5} metalness={0.4} />
          </mesh>
        </group>

        {/* Pendant lines */}
        <mesh
          position={[0, boomBase + MAST_LEN * 0.55, BODY_D_CRN * 0.1]}
          rotation={[0.4, 0, 0.06]}
        >
          <boxGeometry args={[0.5, MAST_LEN * 0.9, 0.5]} />
          <meshStandardMaterial color="#aaa" roughness={0.4} metalness={0.5} />
        </mesh>
      </group>
    </group>
  );
}

function CapacityRings({ craneX, craneY, rings }: {
  craneX: number; craneY: number;
  rings: ReturnType<typeof filteredRings>;
}) {
  const objects = useMemo(() => rings.map(ring => {
    const r = ring.radiusIn * INCH;
    const color = ring.capacityT >= 150 ? '#44bb66' : ring.capacityT >= 60 ? '#ddaa22' : '#dd4422';
    const pts: THREE.Vector3[] = [];
    const N = 96;
    for (let i = 0; i <= N; i++) {
      const a = (i / N) * Math.PI * 2;
      pts.push(new THREE.Vector3(craneX * INCH + Math.cos(a) * r, 0.02, craneY * INCH + Math.sin(a) * r));
    }
    const geo = new THREE.BufferGeometry().setFromPoints(pts);
    const mat = new THREE.LineBasicMaterial({ color, opacity: 0.6, transparent: true });
    return { key: ring.radiusM, line: new THREE.Line(geo, mat), color, r, capacityT: ring.capacityT };
  }), [craneX, craneY, rings]);

  return (
    <>
      {objects.map(({ key, line, color, r, capacityT }) => (
        <group key={key}>
          <primitive object={line} />
          <Text
            position={[craneX * INCH + r + 2, 0.05, craneY * INCH]}
            rotation={[-Math.PI / 2, 0, 0]}
            fontSize={8}
            color={color}
            anchorX="left"
            anchorY="middle"
            outlineWidth={0.5}
            outlineColor="#000000"
          >
            {capacityT * 2}k
          </Text>
        </group>
      ))}
    </>
  );
}

// ── Install location arrows — dashed lines from cast position to wall position ─
const ARROW_Y = 1.5; // ft above slab — above panel tops (~0.6 ft)

function InstallArrows({ placed, installedPositions }: {
  placed: CastPlacedPanel[];
  installedPositions: Map<string, InstalledPosition>;
}) {
  const cones = useMemo(() => {
    return placed.flatMap(pp => {
      const inst = installedPositions.get(pp.panel.id);
      if (!inst) return [];
      const dx = inst.x - pp.cx;
      const dy = inst.y - pp.cy;
      const len = Math.hypot(dx, dy);
      if (len < 1) return [];

      const hue = 120 - (pp.pickOrder / Math.max(placed.length, 1)) * 120;
      const color = new THREE.Color().setHSL(hue / 360, 0.8, 0.55);

      const ux = dx / len, uz = dy / len;
      const coneGeo = new THREE.ConeGeometry(1.8, 5, 8);
      const coneMat = new THREE.MeshBasicMaterial({ color, depthTest: false, transparent: true, opacity: 0.95 });
      const cone = new THREE.Mesh(coneGeo, coneMat);
      cone.position.set(inst.x * INCH, ARROW_Y, inst.y * INCH);
      cone.renderOrder = 100;
      cone.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), new THREE.Vector3(ux, 0, uz));
      return [{ key: pp.panel.id, cone,
        from: [pp.cx * INCH, ARROW_Y, pp.cy * INCH] as [number, number, number],
        to:   [inst.x * INCH, ARROW_Y, inst.y * INCH] as [number, number, number],
        color: `#${color.getHexString()}`,
      }];
    });
  }, [placed, installedPositions]);

  return (
    <>
      {cones.map(({ key, cone, from, to, color }) => (
        <group key={key}>
          <Line
            points={[from, to]}
            color={color}
            lineWidth={4}
            dashed
            dashSize={3}
            gapSize={1.5}
            transparent
            opacity={0.85}
            depthTest={false}
            renderOrder={100}
          />
          <primitive object={cone} />
        </group>
      ))}
    </>
  );
}

// ── Scene content (uses useFrame, must be inside Canvas) ─────────────────────
function SceneContent({
  result, slabCorners, settings,
  currentPickStep, allPickSteps, installedPositions,
  selectedPanelIds, onPanelSelect, onPanelMove, onPanelRotate, onPanelDrop,
  setRubberBand3D, onRubberBandSelect,
  sizeRef, centerRef, zoomRef, panRef, fitViewRef, onManualMove,
}: {
  result: CastLayoutResult;
  slabCorners: [number, number][];
  settings: CastLayoutSettings;
  currentPickStep?: PickStep | null;
  allPickSteps?: PickStep[];
  installedPositions?: Map<string, InstalledPosition>;
  selectedPanelIds?: Set<string>;
  onPanelSelect?: (id: string | null) => void;
  onPanelMove?: (id: string, cx: number, cy: number) => void;
  onPanelRotate?: (id: string, rotRad: number) => void;
  onPanelDrop?: (id: string) => void;
  setRubberBand3D?: (rb: { x1: number; y1: number; x2: number; y2: number } | null) => void;
  onRubberBandSelect?: (ids: string[]) => void;
  sizeRef: { current: { sx: number; sy: number; sz: number } };
  centerRef: { current: { x: number; y: number; z: number } };
  zoomRef: React.MutableRefObject<((f: number) => void) | null>;
  panRef: React.MutableRefObject<((dx: number, dy: number) => void) | null>;
  fitViewRef: React.MutableRefObject<(() => void) | null>;
  onManualMove: () => void;
}) {
  const { placed, craneX, craneY } = result;
  const rings = useMemo(() => filteredRings(settings.craneModel, 3), [settings.craneModel]);
  const { ui } = useShallowStore(s => ({ ui: s.ui }));

  // 3D drag via ground-plane raycasting
  const { gl, camera, raycaster } = useThree();
  const dragRef3D = useRef<{ panelId: string; offsetX: number; offsetZ: number } | null>(null);
  const orbitDisabledRef = useRef(false);
  const groundPlane = useMemo(() => new THREE.Plane(new THREE.Vector3(0, 1, 0), 0), []);
  // Set to true when a panel pointerdown fires (used in pointerup to distinguish panel click vs canvas click)
  const pointerOnPanelRef = useRef(false);

  // Stable refs so pointer-event useEffect can stay with empty dep array
  const selectedPanelIdsRef = useRef(selectedPanelIds ?? new Set<string>());
  useEffect(() => { selectedPanelIdsRef.current = selectedPanelIds ?? new Set(); }, [selectedPanelIds]);

  const resultRef = useRef(result);
  resultRef.current = result;

  const setRubberBand3DRef = useRef(setRubberBand3D);
  useEffect(() => { setRubberBand3DRef.current = setRubberBand3D; }, [setRubberBand3D]);

  const onRubberBandSelectRef = useRef(onRubberBandSelect);
  useEffect(() => { onRubberBandSelectRef.current = onRubberBandSelect; }, [onRubberBandSelect]);

  const onPanelSelectRef = useRef(onPanelSelect);
  useEffect(() => { onPanelSelectRef.current = onPanelSelect; }, [onPanelSelect]);

  useEffect(() => {
    const el = gl.domElement;

    let rbStart: { clientX: number; clientY: number } | null = null;
    let downPos: { x: number; y: number } | null = null;

    function onPointerDown(e: PointerEvent) {
      downPos = { x: e.clientX, y: e.clientY };
      pointerOnPanelRef.current = false;

      // Rubber band: Shift + left click on empty space when panels are already selected
      if (
        e.button === 0 &&
        e.shiftKey &&
        selectedPanelIdsRef.current.size > 0 &&
        !dragRef3D.current
      ) {
        rbStart = { clientX: e.clientX, clientY: e.clientY };
        orbitDisabledRef.current = true;
        const rect = el.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        setRubberBand3DRef.current?.({ x1: x, y1: y, x2: x, y2: y });
        el.setPointerCapture(e.pointerId);
        return;
      }
    }

    function onPointerMove(e: PointerEvent) {
      if (rbStart) {
        const rect = el.getBoundingClientRect();
        const x1 = rbStart.clientX - rect.left;
        const y1 = rbStart.clientY - rect.top;
        const x2 = e.clientX - rect.left;
        const y2 = e.clientY - rect.top;
        setRubberBand3DRef.current?.({ x1, y1, x2, y2 });
        return;
      }

      if (!dragRef3D.current || !onPanelMove) return;
      const rect = el.getBoundingClientRect();
      const ndc = new THREE.Vector2(
        ((e.clientX - rect.left) / rect.width) * 2 - 1,
        -((e.clientY - rect.top) / rect.height) * 2 + 1,
      );
      raycaster.setFromCamera(ndc, camera);
      const hit = new THREE.Vector3();
      if (raycaster.ray.intersectPlane(groundPlane, hit)) {
        onPanelMove(
          dragRef3D.current.panelId,
          (hit.x - dragRef3D.current.offsetX) / INCH,
          (hit.z - dragRef3D.current.offsetZ) / INCH,
        );
      }
    }

    function onPointerUp(e: PointerEvent) {
      if (rbStart) {
        const rect = el.getBoundingClientRect();
        const x1 = rbStart.clientX - rect.left;
        const y1 = rbStart.clientY - rect.top;
        const x2 = e.clientX - rect.left;
        const y2 = e.clientY - rect.top;
        setRubberBand3DRef.current?.(null);
        rbStart = null;

        // Only select if rubber band is large enough (not just a click)
        if (Math.abs(x2 - x1) > 5 || Math.abs(y2 - y1) > 5) {
          const ndcX1 = Math.min(x1, x2) / rect.width  * 2 - 1;
          const ndcX2 = Math.max(x1, x2) / rect.width  * 2 - 1;
          const ndcY1 = -(Math.min(y1, y2) / rect.height * 2 - 1);
          const ndcY2 = -(Math.max(y1, y2) / rect.height * 2 - 1);
          const ndcYMin = Math.min(ndcY1, ndcY2);
          const ndcYMax = Math.max(ndcY1, ndcY2);

          const selected: string[] = [];
          for (const pp of resultRef.current.placed) {
            const vec = new THREE.Vector3(pp.cx * INCH, 0, pp.cy * INCH);
            vec.project(camera);
            if (vec.x >= ndcX1 && vec.x <= ndcX2 && vec.y >= ndcYMin && vec.y <= ndcYMax) {
              selected.push(pp.panel.id);
            }
          }
          onRubberBandSelectRef.current?.(selected);
        }
        orbitDisabledRef.current = false;
        return;
      }

      if (dragRef3D.current) {
        onPanelDrop?.(dragRef3D.current.panelId);
        dragRef3D.current = null;
        orbitDisabledRef.current = false;
        downPos = null;
        return;
      }

      // Click on empty space (no panel drag, small movement) → deselect all
      if (
        downPos &&
        Math.hypot(e.clientX - downPos.x, e.clientY - downPos.y) < 5 &&
        !pointerOnPanelRef.current
      ) {
        onPanelSelectRef.current?.(null);
      }
      downPos = null;
      orbitDisabledRef.current = false;
    }

    el.addEventListener('pointerdown', onPointerDown);
    el.addEventListener('pointermove', onPointerMove);
    el.addEventListener('pointerup', onPointerUp);
    return () => {
      el.removeEventListener('pointerdown', onPointerDown);
      el.removeEventListener('pointermove', onPointerMove);
      el.removeEventListener('pointerup', onPointerUp);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gl.domElement, camera, raycaster, onPanelMove, onPanelDrop, groundPlane]);

  function handlePanelDown3D(panelId: string, offsetX: number, offsetZ: number) {
    orbitDisabledRef.current = true;
    pointerOnPanelRef.current = true;
    dragRef3D.current = { panelId, offsetX, offsetZ };
    onPanelSelect?.(panelId);
  }

  // Determine active panel and done panels
  const activePanelId = currentPickStep?.panelId ?? null;

  function isPanelDone(panelId: string): boolean {
    if (!currentPickStep || !allPickSteps) return false;
    const last = [...allPickSteps].reverse().find((s: PickStep) => s.panelId === panelId && s.phase === 6);
    return last !== undefined && last.stepIndex < currentPickStep.stepIndex;
  }

  // Crane facing: during animation use current step's arm angle; otherwise face first panel
  const facingAngle = useMemo(() => {
    if (currentPickStep) {
      return bearingToThreeY(currentPickStep.craneArmAngleRad);
    }
    if (placed.length === 0) return 0;
    const first = placed[0];
    const dX = first.cx * INCH - craneX * INCH;
    const dZ = first.cy * INCH - craneY * INCH;
    return Math.atan2(dX, dZ);
  }, [currentPickStep, placed, craneX, craneY]);

  // Compute pick point world positions for rigging cables
  // These are approximate — full inverse-transform is complex; use hook-to-panel-center cables
  const riggingPoints = useMemo((): THREE.Vector3[] => {
    if (!currentPickStep || (currentPickStep.phase !== 2 && currentPickStep.phase !== 3 && currentPickStep.phase !== 4 && currentPickStep.phase !== 5)) return [];
    const pp = placed.find(p => p.panel.id === currentPickStep.panelId);
    if (!pp) return [];
    const cx = currentPickStep.panelCx * INCH;
    const cy = currentPickStep.panelCy * INCH;
    const elevFt = currentPickStep.panelElevFt;
    const pickHeight = elevFt + pp.h * INCH * 0.75; // pick points near top of panel
    const picks = pp.panel.ppvar.filter(s => s.enabled);
    if (picks.length === 0) return [new THREE.Vector3(cx, pickHeight, cy)];
    // Transform pick points: for simplicity place them at panel_cx ± offset along panel width
    return picks.map(s => {
      const lx = (s.data.x - pp.w / 2) * INCH;
      return new THREE.Vector3(
        cx + lx * Math.cos(-pp.rotationRad),
        pickHeight,
        cy + lx * Math.sin(-pp.rotationRad),
      );
    });
  }, [currentPickStep, placed]);

  // Brace info for current panel during phase 5-6
  const braceInfo = useMemo(() => {
    if (!currentPickStep || (currentPickStep.phase !== 5 && currentPickStep.phase !== 6)) return null;
    const pp = placed.find(p => p.panel.id === currentPickStep.panelId);
    const inst = installedPositions?.get(currentPickStep.panelId);
    if (!pp || !inst) return null;
    return {
      instX: inst.x, instY: inst.y,
      w: pp.w, h: pp.h,
      wallAngle: inst.wallAngle,
      braceCount: pp.panel.bpvar.filter(s => s.enabled).length,
    };
  }, [currentPickStep, placed, installedPositions]);

  return (
    <>
      <OrbitCameraRig
        preset={ui.viewPreset}
        contentKey={`${slabCorners.length}-${placed.length}`}
        sizeRef={sizeRef}
        centerRef={centerRef}
        zoomRef={zoomRef}
        panRef={panRef}
        fitViewRef={fitViewRef}
        onManualMove={onManualMove}
        disabledRef={orbitDisabledRef}
      />

      <ambientLight intensity={0.6} />
      <directionalLight position={[3000, 5000, 2000]} intensity={1.2} castShadow />
      <directionalLight position={[-2000, 1000, -1000]} intensity={0.4} />

      {ui.shadingMode === 'shaded' && <Environment preset="warehouse" background={false} />}

      <SlabMesh corners={slabCorners} />

      {/* Panels: active = AnimatedPanel, done = InstalledPanel, future = FlatPanel */}
      {placed.map(pp => {
        if (pp.panel.id === activePanelId && currentPickStep) {
          const inst = installedPositions?.get(pp.panel.id) ?? null;
          return (
            <AnimatedPanel
              key={pp.panel.id}
              pp={pp}
              step={currentPickStep}
              instPos={inst}
            />
          );
        }
        if (isPanelDone(pp.panel.id)) {
          const inst = installedPositions?.get(pp.panel.id);
          if (inst) {
            return (
              <InstalledPanel
                key={pp.panel.id + '-inst'}
                instX={inst.x} instY={inst.y}
                w={pp.w} h={pp.h}
                wallAngle={inst.wallAngle}
                panel={pp.panel}
              />
            );
          }
        }
        return (
          <FlatPanel
            key={pp.panel.id + '-' + pp.stackLevel}
            panelId={pp.panel.id}
            cx={pp.cx} cy={pp.cy}
            w={pp.w} h={pp.h}
            rotationRad={pp.rotationRad}
            isStacked={pp.isStacked}
            stackLevel={pp.stackLevel}
            panel={pp.panel}
            weightTons={pp.weightTons}
            pickOrder={pp.pickOrder}
            dimmed={activePanelId !== null && !isPanelDone(pp.panel.id)}
            isSelected={selectedPanelIds?.has(pp.panel.id) ?? false}
            onPanelDown={handlePanelDown3D}
            onPanelRotate={onPanelRotate}
          />
        );
      })}

      {/* Install arrows (when enabled and not animating) */}
      {installedPositions && !currentPickStep && (
        <InstallArrows placed={placed} installedPositions={installedPositions} />
      )}

      {settings.showCapacityRings && (
        <CapacityRings craneX={craneX} craneY={craneY} rings={rings} />
      )}

      <CraneMesh craneX={craneX} craneY={craneY} facingAngle={facingAngle} />

      {/* Rigging cables during phases 2–5 */}
      {riggingPoints.length > 0 && (
        <RiggingCables
          craneX={craneX} craneY={craneY}
          facingAngle={facingAngle}
          panelPickPoints={riggingPoints}
        />
      )}

      {/* Brace lines during phases 5–6 */}
      {braceInfo && (
        <BraceLines
          instX={braceInfo.instX} instY={braceInfo.instY}
          w={braceInfo.w} h={braceInfo.h}
          wallAngle={braceInfo.wallAngle}
          braceCount={braceInfo.braceCount}
        />
      )}

      {(() => {
        const SECTION = 1200 * INCH; // 100 ft
        const ge = slabCorners.length >= 3
          ? snapGridExtent(
              Math.min(...slabCorners.map(c => c[0])) * INCH,
              Math.max(...slabCorners.map(c => c[0])) * INCH,
              Math.min(...slabCorners.map(c => c[1])) * INCH,
              Math.max(...slabCorners.map(c => c[1])) * INCH,
              SECTION,
            )
          : snapGridExtent(-SECTION, SECTION, -SECTION, SECTION, SECTION);
        return (
          <>
            <NorthArrow
              size={SECTION * 0.3}
              gridSection={SECTION}
              origin={[ge.xStart, ge.zStart]}
            />

            {ui.showGround && (
              <mesh rotation={[-Math.PI / 2, 0, 0]} position={[ge.cx, -0.5, ge.cz]} receiveShadow>
                <planeGeometry args={[ge.width, ge.depth]} />
                <meshStandardMaterial color="#2a2a2a" roughness={0.95} />
              </mesh>
            )}

            <Grid
              args={[ge.width, ge.depth]}
              cellSize={120 * INCH}
              cellThickness={0}
              cellColor="#3a5a80"
              sectionSize={SECTION}
              sectionThickness={2.5}
              sectionColor="#5080b0"
              fadeDistance={50_000}
              visible={ui.showGrid}
              position={[ge.cx, 0, ge.cz]}
            />

            {ui.showGrid && <GridLabels extent={ge} sectionSize={SECTION} fontSize={SECTION * 0.12} />}
          </>
        );
      })()}
    </>
  );
}

// ── Main export ───────────────────────────────────────────────────────────────
export default function CastLayout3D({
  result, slabCorners, settings,
  currentPickStep, allPickSteps, installedPositions,
  selectedPanelIds, onPanelSelect, onRubberBandSelect, onPanelMove, onPanelRotate, onPanelDrop,
}: Props) {
  const { clearViewPreset } = useShallowStore(s => ({
    clearViewPreset: s.clearViewPreset,
  }));

  const [rubberBand3D, setRubberBand3D] = useState<{
    x1: number; y1: number; x2: number; y2: number;
  } | null>(null);

  const zoomRef    = useRef<((factor: number) => void) | null>(null);
  const panRef     = useRef<((dx: number, dy: number) => void) | null>(null);
  const fitViewRef = useRef<(() => void) | null>(null);
  const viewerRef  = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = viewerRef.current;
    if (!el) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'h' || e.key === 'H' || e.key === 'f' || e.key === 'F') {
        clearViewPreset(); fitViewRef.current?.();
      } else if (e.key === '+' || e.key === '=') { zoomRef.current?.(0.8); clearViewPreset(); }
      else if (e.key === '-') { zoomRef.current?.(1.25); clearViewPreset(); }
    };
    el.addEventListener('keydown', onKey);
    return () => el.removeEventListener('keydown', onKey);
  }, [clearViewPreset]);

  const { boundingCenter, boundingSize } = useMemo(() => {
    if (slabCorners.length === 0) {
      return {
        boundingCenter: { x: 0, y: 5, z: 0 },
        boundingSize:   { sx: 120, sy: 10, sz: 120 },
      };
    }
    let minX = Infinity, maxX = -Infinity, minZ = Infinity, maxZ = -Infinity;
    for (const [x, y] of slabCorners) {
      if (x < minX) minX = x; if (x > maxX) maxX = x;
      if (y < minZ) minZ = y; if (y > maxZ) maxZ = y;
    }
    return {
      boundingCenter: { x: (minX + maxX) / 2 * INCH, y: 5, z: (minZ + maxZ) / 2 * INCH },
      boundingSize:   { sx: (maxX - minX) * INCH, sy: 10, sz: (maxZ - minZ) * INCH },
    };
  }, [slabCorners]);

  const sizeRef   = useRef(boundingSize);
  const centerRef = useRef(boundingCenter);
  sizeRef.current   = boundingSize;
  centerRef.current = boundingCenter;

  const isAnimating = currentPickStep != null;

  return (
    <div className="viewer-root" tabIndex={-1} ref={viewerRef}>
      <ViewerToolbar
        onCenterView={() => { clearViewPreset(); fitViewRef.current?.(); }}
        onZoomIn={() => { zoomRef.current?.(0.8); clearViewPreset(); }}
        onZoomOut={() => { zoomRef.current?.(1.25); clearViewPreset(); }}
        onPanLeft={() => { panRef.current?.(30, 0); clearViewPreset(); }}
        onPanRight={() => { panRef.current?.(-30, 0); clearViewPreset(); }}
        onPanUp={() => { panRef.current?.(0, -30); clearViewPreset(); }}
        onPanDown={() => { panRef.current?.(0, 30); clearViewPreset(); }}
      />
      <div style={{ position: 'relative', width: '100%', height: '100%' }}>
        <Canvas
          frameloop={isAnimating ? 'always' : 'demand'}
          camera={{ fov: 45, near: 0.1, far: 1_000_000, position: [0, 0, 500] }}
          gl={{ antialias: true }}
          shadows
          style={{ background: '#0d1117' }}
        >
          <SceneContent
            result={result}
            slabCorners={slabCorners}
            settings={settings}
            currentPickStep={currentPickStep}
            allPickSteps={allPickSteps}
            installedPositions={installedPositions}
            selectedPanelIds={selectedPanelIds}
            onPanelSelect={onPanelSelect}
            onRubberBandSelect={onRubberBandSelect}
            onPanelMove={onPanelMove}
            onPanelRotate={onPanelRotate}
            onPanelDrop={onPanelDrop}
            setRubberBand3D={setRubberBand3D}
            sizeRef={sizeRef}
            centerRef={centerRef}
            zoomRef={zoomRef}
            panRef={panRef}
            fitViewRef={fitViewRef}
            onManualMove={clearViewPreset}
          />
        </Canvas>
        {rubberBand3D && (
          <div
            style={{
              position: 'absolute',
              pointerEvents: 'none',
              left:   Math.min(rubberBand3D.x1, rubberBand3D.x2),
              top:    Math.min(rubberBand3D.y1, rubberBand3D.y2),
              width:  Math.abs(rubberBand3D.x2 - rubberBand3D.x1),
              height: Math.abs(rubberBand3D.y2 - rubberBand3D.y1),
              border: '2px dashed #4da6ff',
              background: 'rgba(77,166,255,0.08)',
            }}
          />
        )}
      </div>
    </div>
  );
}
